import { Icon } from '@chiroup/components/Icon';
import { Icons } from '@chiroup/components/icons';
import * as Sentry from '@sentry/react';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { usePatientPortal } from '../../contexts/PatientPortalContext';
import { useInvalidateAppointmentSurveysOnUnmount } from '../../hooks/useAppointmentSurveys';
import surveyService, { ConsentDetail } from '../../services/survey.service';
import { Skeleton } from '../common/Skeleton';
import { ConsentPanelStatus, SoftConsentPanel } from './consentPanels';

const verbiage = {
  en: {
    back: 'Back',
    notFoundTitle: 'Consent not found',
    notFoundDetail: 'It may have been withdrawn or be unavailable for this account.',
  },
  es: {
    back: 'Volver',
    notFoundTitle: 'Consentimiento no encontrado',
    notFoundDetail: 'Es posible que se haya retirado o que no esté disponible para esta cuenta.',
  },
};

type RunnerStatus =
  | 'loading'
  | 'ready'
  | 'submitting'
  | 'submitted'
  | 'error'
  | 'notfound'
  | 'alreadyComplete';

const ConsentRunner: React.FC<{ basePath: string }> = ({ basePath }) => {
  const { patientConsentId } = useParams<{ patientConsentId: string }>();
  const navigate = useNavigate();
  const { selectedPatient } = usePatientPortal();
  const primaryColor = selectedPatient?.clinic?.primaryColor || '#77bd43';
  const clinicPath = (selectedPatient?.clinic as { path?: string } | undefined)?.path;

  // Refresh Home's pre-visit task list when leaving — otherwise the
  // 30s staleTime keeps the cached entry showing as not-complete.
  useInvalidateAppointmentSurveysOnUnmount();

  const [status, setStatus] = useState<RunnerStatus>('loading');
  const [detail, setDetail] = useState<ConsentDetail | null>(null);

  const language: 'en' | 'es' = (detail?.language === 'es' ? 'es' : 'en') as 'en' | 'es';
  const v = useMemo(() => verbiage[language], [language]);

  useEffect(() => {
    let cancelled = false;

    if (!clinicPath || !patientConsentId) {
      setStatus('notfound');

      return;
    }

    setStatus('loading');

    surveyService
      .getConsent(clinicPath, patientConsentId)
      .then(res => {
        if (cancelled) return;
        setDetail(res);
        setStatus(res.completedAt ? 'alreadyComplete' : 'ready');
      })
      .catch(err => {
        if (cancelled) return;
        const code = err?.response?.status || err?.statusCode;

        if (code === 404) {
          setStatus('notfound');
        } else {
          Sentry.captureException(err);
          setStatus('error');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [clinicPath, patientConsentId]);

  const exitToHome = () => navigate(basePath || '/');

  const handleSubmit = async (signature: string) => {
    if (!detail || !clinicPath) return;

    setStatus('submitting');

    try {
      await surveyService.submitConsent({
        path: clinicPath,
        patientConsentId: detail.id,
        signature,
      });
      setStatus('submitted');
    } catch (err) {
      Sentry.captureException(err);
      setStatus('error');
    }
  };

  return (
    <div className="flex flex-col">
      <div className="mb-4">
        <Link
          to={basePath || '/'}
          className="inline-flex items-center gap-1 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
        >
          <Icon icon={Icons.chevronLeft} className="size-4" />
          {v.back}
        </Link>
      </div>

      {status === 'loading' && (
        <div className="flex flex-col gap-4">
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-96 w-full rounded-2xl" />
        </div>
      )}

      {status === 'notfound' && (
        <div className="portal-card p-6 text-center">
          <p className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1">
            {v.notFoundTitle}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{v.notFoundDetail}</p>
        </div>
      )}

      {detail && status !== 'loading' && status !== 'notfound' && (
        <SoftConsentPanel
          title={detail.title}
          url={detail.url}
          primaryColor={primaryColor}
          language={language}
          status={
            (status === 'alreadyComplete'
              ? 'submitted'
              : status === 'ready'
                ? 'ready'
                : status === 'submitting'
                  ? 'submitting'
                  : status === 'submitted'
                    ? 'submitted'
                    : 'error') as ConsentPanelStatus
          }
          onSubmit={handleSubmit}
          onExit={exitToHome}
          exitLabel={language === 'es' ? 'Volver al inicio' : 'Back to home'}
        />
      )}
    </div>
  );
};

export default ConsentRunner;
